import React, { useState, useEffect } from "react";
import useSWR from "swr";
import { useVisitorDetails } from "./VisitorDetailsContext";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import TopActionMenus from "./TopActionMenus";
import DashboardContentFooter from "./DashboardContentFooter";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigationEvent } from "navigation-react";
import LoaderAnimation from "./LoaderAnimation";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });
const BASE_URL = process.env.REACT_APP_DRUPAL_BASE_URL || "https://svms.salcommsystems.click";

// SWR fetcher with credentials included
const fetcher = (url) =>
  fetch(url, { credentials: "include" }).then((res) => {
    if (!res.ok) throw new Error(`Fetch error ${res.status} ${res.statusText}`);
    return res.json();
  });

// Function to get authentication headers
const getAuthHeaders = () => {
  const csrfToken = ls.get("usercsrf_token");
  if (!csrfToken) throw new Error("No authentication credentials available");
  return {
    "Content-Type": "application/vnd.api+json",
    Accept: "application/vnd.api+json",
    "X-CSRF-Token": csrfToken,
  };
};

export default function VisitorEditSignRegister() {
  const { stateNavigator } = useNavigationEvent();
  const { selectedVisitor } = useVisitorDetails();

  const [purposeValue, setPurposeValue] = useState("");
  const [locationValue, setLocationValue] = useState("");
  const [titleValue, setTitleValue] = useState("");
  const [fullnameValue, setFullnameValue] = useState("");
  const [companyValue, setCompanyValue] = useState("");
  const [contactNumberValue, setContactNumberValue] = useState("");
  const [staffValue, setStaffValue] = useState("");
  const [nationalityValue, setNationalityValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API endpoints
  const NATIONALITY_API = `${BASE_URL}/jsonapi/taxonomy_term/nationality`;
  const VISIT_TYPES_API = `${BASE_URL}/jsonapi/taxonomy_term/visit_types`;
  const TITLE_API = `${BASE_URL}/jsonapi/taxonomy_term/visitor_title`;
  const STAFF_API = `${BASE_URL}/jsonapi/user/user`;
  const VISITOR_PATCH_API = (uuid) => `${BASE_URL}/jsonapi/node/visitor_attendance/${uuid}`;

  // Fetch options for selects
  const { data: nationalityData } = useSWR(NATIONALITY_API, fetcher);
  const { data: visitTypesData } = useSWR(VISIT_TYPES_API, fetcher);
  const { data: titleTypesData } = useSWR(TITLE_API, fetcher);
  const { data: staffData } = useSWR(STAFF_API, fetcher);

  const nationalityItems = nationalityData?.data?.map((term) => ({
    label: term.attributes.name,
    value: term.id,
  })) || [];

  const purposeItems = visitTypesData?.data?.map((term) => ({
    label: term.attributes.name,
    value: term.id,
  })) || [];

  const titleItems = titleTypesData?.data?.map((term) => ({
    label: term.attributes.name,
    value: term.id,
  })) || [];

  const staffItems = staffData?.data?.map((user) => {
    const firstName = user.attributes?.field_firist_name || "";
    const lastName = user.attributes?.field_last_name || "";
    return {
      label: `${firstName} ${lastName}`.trim() || user.attributes.name,
      value: user.id,
    };
  }) || [];

  // Redirect if not logged in
  useEffect(() => {
    const csrfToken = ls.get("usercsrf_token");
    const username = ls.get("username");
    if (!csrfToken || !username) {
      alert("You are not logged in. Redirecting to login page.");
      stateNavigator.navigate("login");
    }
  }, [stateNavigator]);

  // Populate form fields from selected visitor
  useEffect(() => {
    if (!selectedVisitor) {
      stateNavigator.navigate("visitorsignregister");
      return;
    }
    setPurposeValue(selectedVisitor?.uuid_3 || "");
    setLocationValue(selectedVisitor?.field_visitor_location || "");
    setTitleValue(selectedVisitor?.uuid_1 || "");
    setFullnameValue(selectedVisitor?.field_full_name || "");
    setCompanyValue(selectedVisitor?.field_company || "");
    setContactNumberValue(selectedVisitor?.field_contact_number || "");
    setStaffValue(selectedVisitor?.uuid_4 || "");
    setNationalityValue(selectedVisitor?.uuid_2 || "");
  }, [selectedVisitor, stateNavigator]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVisitor?.uuid) {
      alert("No visitor selected");
      return;
    }

    if (!locationValue || !purposeValue || !staffValue) {
      alert("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const headers = getAuthHeaders();

      console.log("🔹 Cookies before PATCH:", document.cookie);

      const payload = {
        data: {
          type: "node--visitor_attendance",
          id: selectedVisitor.uuid,
          attributes: {
            title: fullnameValue,
            field_full_name: fullnameValue,
            field_company: companyValue,
            field_contact_number: contactNumberValue,
            field_visitor_location: locationValue,
          },
          relationships: {
            field_purpose_of_visit: { data: { type: "taxonomy_term--visit_types", id: purposeValue } },
            field_staff_to_visit: { data: { type: "user--user", id: staffValue } },
            field_nationality: { data: { type: "taxonomy_term--nationality", id: nationalityValue } },
            field_title_prefix: { data: { type: "taxonomy_term--visitor_title", id: titleValue } },
          },
        },
      };

      const response = await fetch(VISITOR_PATCH_API(selectedVisitor.uuid), {
        method: "PATCH",
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error("Visitor update failed:", errText);
        alert(`Failed to update visitor: ${errText}`);
        setIsSubmitting(false);
        return;
      }

      const result = await response.json();
      console.log("Visitor updated:", result);

      alert("Visitor updated successfully");
      setIsSubmitting(false);
      stateNavigator.navigate("visitorsignregister");
    } catch (err) {
      console.error("Update error:", err);
      alert(`Error updating visitor: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  if (!titleItems.length && !purposeItems.length && !staffItems.length && !nationalityItems.length) {
    return <LoaderAnimation loadingTxt="Loading form options..." />;
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="patient-register">
          <TopActionMenus
            AddTitle="Edit Visitor details"
            backIcon={faArrowLeft}
            backNavigationLink="visitorsignregister"
          />
          <form className="patient-form" onSubmit={handleSubmit} style={{ width: "60%", margin: "0 auto" }}>
            {/* Form fields remain the same */}
            {/* Title */}
            <div className="form-group">
              <label>Title</label>
              <select value={titleValue} onChange={(e) => setTitleValue(e.target.value)} required>
                <option value="">Select Title</option>
                {titleItems.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>

            {/* Full Name */}
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" value={fullnameValue} onChange={(e) => setFullnameValue(e.target.value)} required />
            </div>

            {/* Company */}
            <div className="form-group">
              <label>Company</label>
              <input type="text" value={companyValue} onChange={(e) => setCompanyValue(e.target.value)} required />
            </div>

            {/* Nationality */}
            <div className="form-group">
              <label>Nationality</label>
              <select value={nationalityValue} onChange={(e) => setNationalityValue(e.target.value)} required>
                <option value="">Select your nationality</option>
                {nationalityItems.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>

            {/* Contact Number */}
            <div className="form-group">
              <label>Contact Number</label>
              <input type="tel" value={contactNumberValue} onChange={(e) => setContactNumberValue(e.target.value)} required />
            </div>

            {/* Location */}
            <div className="form-group">
              <label>Location</label>
              <input type="text" value={locationValue} onChange={(e) => setLocationValue(e.target.value)} required />
            </div>

            {/* Purpose */}
            <div className="form-group">
              <label>Purpose of Visit</label>
              <select value={purposeValue} onChange={(e) => setPurposeValue(e.target.value)} required>
                <option value="">Select Purpose</option>
                {purposeItems.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>

            {/* Staff */}
            <div className="form-group">
              <label>Who to Visit</label>
              <select value={staffValue} onChange={(e) => setStaffValue(e.target.value)} required>
                <option value="">Select Staff</option>
                {staffItems.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Visitor"}
            </button>
          </form>
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
}
